<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto"
  >
    <div
      class="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transform transition-all my-8"
    >
      <!-- Modal Header -->
      <div
        class="px-6 py-5 bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white flex items-center justify-between"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20"
          >
            <Sliders class="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 class="text-lg font-bold">Configure Student Fees</h3>
            <p class="text-xs text-indigo-100 font-medium">
              Set admission & recurring monthly fee structures
            </p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Student Info Banner -->
      <div
        v-if="student"
        class="bg-indigo-50/70 border-b border-indigo-100 px-6 py-3 flex items-center justify-between"
      >
        <div>
          <span class="text-xs font-semibold text-indigo-600 uppercase tracking-wider"
            >Student</span
          >
          <h4 class="text-sm font-bold text-slate-800">{{ student.name }}</h4>
        </div>
        <div class="text-right">
          <span class="text-xs text-slate-500">Roll: {{ student.rollNumber || "N/A" }}</span>
          <p class="text-xs font-medium text-slate-700">
            {{ student.class?.name }}
            {{ student.class?.section ? `(${student.class.section})` : "" }}
          </p>
        </div>
      </div>

      <!-- Form Body -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-5">
        <!-- Admission Fee Section -->
        <div class="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
              <GraduationCap class="w-4 h-4 text-indigo-600" />
              One-Time Admission Fee (₹)
            </label>
            <span class="text-[11px] text-slate-500">Charged once per student</span>
          </div>
          <div class="relative">
            <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400 font-bold">₹</span>
            <input
              v-model.number="form.admissionFeeAmount"
              type="number"
              min="0"
              step="1"
              placeholder="0"
              class="w-full pl-8 pr-4 py-2.5 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            />
          </div>
        </div>

        <!-- Monthly Fee Section -->
        <div class="p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-4">
          <div class="flex items-center justify-between">
            <label class="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar class="w-4 h-4 text-purple-600" />
              Recurring Monthly Fee (₹)
            </label>
            <span class="text-[11px] text-slate-500">Billed each calendar month</span>
          </div>

          <div class="relative">
            <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400 font-bold">₹</span>
            <input
              v-model.number="form.monthlyFeeAmount"
              type="number"
              min="0"
              step="1"
              placeholder="0"
              class="w-full pl-8 pr-4 py-2.5 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
            />
          </div>

          <!-- Billing Start Month and Year -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-600 mb-1">
                Start Month
              </label>
              <select
                v-model.number="form.startMonth"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option v-for="(m, idx) in months" :key="idx" :value="idx + 1">
                  {{ m }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 mb-1">
                Start Year
              </label>
              <input
                v-model.number="form.startYear"
                type="number"
                min="2020"
                max="2035"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>

          <!-- Due Day & Policy -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-600 mb-1">
                Due Day of Month
              </label>
              <input
                v-model.number="form.dueDay"
                type="number"
                min="1"
                max="31"
                placeholder="10"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <span class="text-[10px] text-slate-400">e.g. 10 = 10th of every month</span>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-600 mb-1">
                Joining Billing Policy
              </label>
              <select
                v-model="form.joinPolicy"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="full_month">Full Month Charge</option>
                <option value="next_month">Bill Next Month</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Academic Session & Remarks -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-600 mb-1">
              Academic Session
            </label>
            <input
              v-model="form.academicSession"
              type="text"
              placeholder="e.g. 2026-2027"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-600 mb-1">
              Remarks (Optional)
            </label>
            <input
              v-model="form.remarks"
              type="text"
              placeholder="e.g. Concession granted"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="pt-4 flex items-center justify-end space-x-3 border-t border-slate-200">
          <button
            type="button"
            @click="$emit('close')"
            :disabled="isSubmitting"
            class="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-xl shadow-lg shadow-indigo-500/25 transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <span
              v-if="isSubmitting"
              class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            ></span>
            <span>{{ isSubmitting ? "Saving..." : "Save Configuration" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { reactive, watch } from "vue";
  import { X, Sliders, GraduationCap, Calendar } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    student: { type: Object, default: null },
    feeStructure: { type: Object, default: null },
    isSubmitting: { type: Boolean, default: false },
  });

  const emit = defineEmits(["close", "save"]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const now = new Date();
  const form = reactive({
    admissionFeeAmount: 0,
    monthlyFeeAmount: 0,
    startMonth: now.getMonth() + 1,
    startYear: now.getFullYear(),
    dueDay: 10,
    joinPolicy: "full_month",
    academicSession: "",
    remarks: "",
  });

  watch(
    () => props.feeStructure,
    (fs) => {
      if (fs) {
        form.admissionFeeAmount = fs.admissionFee?.amount || 0;
        form.monthlyFeeAmount = fs.monthlyFee?.amount || 0;
        form.startMonth = fs.monthlyFee?.startMonth || now.getMonth() + 1;
        form.startYear = fs.monthlyFee?.startYear || now.getFullYear();
        form.dueDay = fs.monthlyFee?.dueDay || 10;
        form.joinPolicy = fs.monthlyFee?.joinPolicy || "full_month";
        form.academicSession = fs.academicSession || props.student?.session || "";
        form.remarks = fs.remarks || "";
      } else {
        form.admissionFeeAmount = 0;
        form.monthlyFeeAmount = 0;
        form.startMonth = now.getMonth() + 1;
        form.startYear = now.getFullYear();
        form.dueDay = 10;
        form.joinPolicy = "full_month";
        form.academicSession = props.student?.session || "";
        form.remarks = "";
      }
    },
    { immediate: true }
  );

  const handleSubmit = () => {
    emit("save", { ...form });
  };
</script>
